import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import './ToastNotification.css';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

interface Toast {
  id: number;
  message: string;
  type: ToastType;
  duration?: number;
}

interface ToastContextType {
  showToast: (message: string, type: ToastType, duration?: number) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextIdRef = useRef(1);
  const timeoutHandlesRef = useRef<number[]>([]);

  const showToast = useCallback((message: string, type: ToastType, duration: number = 4000) => {
    const id = nextIdRef.current;
    nextIdRef.current += 1;

    const newToast: Toast = { id, message, type, duration };
    setToasts(prev => [...prev, newToast]);

    const timeoutId = window.setTimeout(() => {
      setToasts(prev => prev.filter(toast => toast.id !== id));
      timeoutHandlesRef.current = timeoutHandlesRef.current.filter(handle => handle !== timeoutId);
    }, duration);

    timeoutHandlesRef.current.push(timeoutId);
  }, []);

  useEffect(() => () => {
    timeoutHandlesRef.current.forEach(handle => {
      window.clearTimeout(handle);
    });
    timeoutHandlesRef.current = [];
  }, []);

  const removeToast = (id: number) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  const getIconClass = (type: ToastType) => {
    switch (type) {
      case 'success':
        return 'toast-icon-success';
      case 'error':
        return 'toast-icon-error';
      case 'warning':
        return 'toast-icon-warning';
      case 'info':
        return 'toast-icon-info';
      default:
        return 'toast-icon-info';
    }
  };

  const getIcon = (type: ToastType) => {
    switch (type) {
      case 'success':
        return '✓';
      case 'error':
        return '✕';
      case 'warning':
        return '⚠';
      case 'info':
        return 'i';
      default:
        return 'i';
    }
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div className="toast-container">
        {toasts.map(toast => (
          <div
            key={toast.id}
            className={`toast toast-${toast.type}`}
          >
            <div className={`toast-icon ${getIconClass(toast.type)}`}>
              {getIcon(toast.type)}
            </div>
            <div className="toast-content">
              <p className="toast-message">{toast.message}</p>
            </div>
            <button
              className="toast-close"
              onClick={() => removeToast(toast.id)}
              aria-label="Close notification"
            >
              ✕
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

